
<!--
  Wager shell — three-way view switch:
    1. `showInitialSkeleton` → first load, render skeleton.
    2. `events.length`       → real content (refreshes keep it).
    3. Otherwise             → empty / error card.
-->
@if (showInitialSkeleton) {
  <ng-container *ngTemplateOutlet="refInitialSkeleton"></ng-container>
}
@else if (events.length) {
  <ng-container *ngTemplateOutlet="refDoneFetchAPI"></ng-container>
}
@else {
  <ng-container *ngTemplateOutlet="refEmptyState"></ng-container>
}

<ng-template #refDoneFetchAPI>
    <section
      class="wager-content tw-px-[28px]"
      scroll-y="false"
    >

      <!-- favorites section -->
      @if (userEmail !== undefined && userEmail !== 'Guest') {
        <div
          class="
            favorites-section
            tw-mt-[12px]
          "
        >
          <img
            src="/assets/images/wager/favorites_icon.svg"
            class="tw-inline tw-mr-[19px] tw-float-left"
          >
          <span
            class="
              tw-text-[18px]
              tw-relative
              tw-top-[3px]
              tw-mt-[5px]
              tw-float-left
            "
          >
            {{ 'ui.wager.favorites' | translate: 'Favorites' }}
          </span>
          <mat-icon
            (click)="scrollTo('prev')"
            class="
              tw-text-[25px]
              tw-text-[#B5C5D8]
              tw-relative
              tw-cursor-pointer
              tw-top-[3px]
              tw-mt-[5px]
              tw-ml-[20px]
              tw-mr-[20px]
              tw-float-left
            "
          >
            arrow_back
          </mat-icon>
          <div #favorites class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left">
            <!-- Skeleton chips while favorites preference resolves. -->
            @if (showFavoritesSkeleton) {
              <ul
                class="tw-text-center tw-flex"
                aria-hidden="true"
                data-cy="wager-favorites-skeleton"
              >
                @for (chip of [1,2,3,4]; track chip) {
                  <li
                    class="
                      wager-skeleton__bar
                      tw-w-[273px]
                      tw-h-[30px]
                      tw-inline-block
                      tw-rounded-[4px]
                      tw-mr-[18px]
                    "
                  ></li>
                }
              </ul>
            }
            @else {
              <ul class="tw-text-center tw-flex">
                <li
                  (click)="selectEvent(favorite)"
                  *ngFor="let favorite of listOfFavoriteEvents"
                  class="
                    tw-text-[#FFFFFF]
                    tw-w-[273px]
                    tw-h-[30px]
                    tw-content-center
                    tw-inline-block
                    tw-rounded-[4px]
                    tw-bg-[#557DAC]
                    tw-mr-[18px]
                  "
                  [ngClass]="{'active-event' : selectedEvent.id === favorite.id}"
                >
                  <!-- Mirror #eventSelection: track (country code), race, status/MTP -->
                  <span data-cy="favorite-event-name">
                    {{
                      favorite?.CountryCode
                        ? favorite?.CountryCode === 'US' || favorite?.CountryCode === 'CA'
                          ? favorite?.ToteResponse?.EventDetail?.EventName
                          : favorite?.CountryCode + '-' + favorite?.ToteResponse?.EventDetail?.EventName
                        : favorite?.ToteResponse?.EventDetail?.EventName
                    }}
                  </span>

                  <span data-cy="favorite-current-race">
                    {{
                      favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId
                        ? '('+(favorite?.ToteResponse?.EventDetail?.TrackType | getTrackType).charAt(0)
                          + ' R'+favorite?.ToteResponse?.EventDetail?.currentRace?.RaceId+')'
                        : ''
                    }}
                  </span>

                  <span data-cy="favorite-event-status">
                    {{
                      favorite?.ToteResponse?.EventDetail?.currentRace
                        ? (
                            (favorite?.ToteResponse?.EventDetail?.currentRace)?.RaceStatus
                              | getRaceStatus: (
                                favorite?.ToteResponse?.EventDetail?.currentRace
                              )?.PostTime: favorite?.eventName
                              | countdown: false: favorite?.eventName | async
                          )
                        : (favorite?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)
                    }}
                  </span>
                </li>
              </ul>
            }
          </div>
          <mat-icon
            (click)="scrollTo('next')"
            class="
              tw-text-[25px]
              tw-text-[#B5C5D8]
              tw-relative
              tw-cursor-pointer
              tw-top-[3px]
              tw-mt-[5px]
              tw-ml-[20px]
              tw-mr-[20px]
              tw-float-left
            "
          >
            arrow_forward
          </mat-icon>
        </div>
      }

      <!-- profile inline panel section -->
      @if (isProfileOpen) {
        <div class="profile-inline-panel tw-mt-[18px]">
          <feature-mat-profile/>
        </div>
      }

      <div class="tw-flex" [style.display]="isProfileOpen ? 'none' : ''">
        <div class="tw-w-[60%] tw-mt-[18px] tw-mr-[20px]">

          <!-- events races pools modifiers section -->
          <div
            class="
              events-races-pools-modifiers-section
              tw-relative
              tw-min-h-[250px]
              tw-rounded-[13px]
              tw-bg-[#EDF1F5]
              tw-mb-[20px]
              tw-pt-[25px]
              tw-pr-[15px]
              tw-pb-[11px]
              tw-pl-[15px]
            "
          >
            <!-- Race selection -->
            <div class="race-section">
              <p class="races-info tw-text-[18px] tw-mt-[6px]" data-cy="races-info">

                <!-- Race status -->
                <span class="highlight tw-text-[#1E3F7F]" data-cy="race-status">
                  {{
                    raceDetails
                      ? (
                          raceDetails?.RaceStatus
                          | getRaceStatus: raceDetails?.PostTime: selectedEvent.eventName
                          | countdown: false: selectedEvent.eventName | async
                        )
                      : (selectedEvent?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)
                  }}
                </span>

                <!-- Race number -->
                <span class="highlight" data-cy="race-number">
                  {{
                    raceDetails?.RaceId
                      ? 'Race: '+raceDetails?.RaceId
                      : ''
                  }}
                </span>

                <!-- Event Track type -->
                <span data-cy="track-type">
                  {{ selectedEvent?.ToteResponse?.EventDetail?.TrackType | getTrackType }}
                </span>

                <!-- Race type -->
                <span data-cy="race-type">
                  {{ raceDetails?.RaceType }}
                </span>

                <!-- Race surface -->
                <span data-cy="race-surface">
                  {{ raceDetails?.Surface }}
                </span>

                <!-- Race distance -->
                <span data-cy="race-distance">
                  {{ raceDetails?.Distance }}
                </span>

                <!-- Race purse -->
                <span class="highlight tw-text-[#1E3F7F]" data-cy="race-purse">
                  {{
                    raceDetails?.Purse
                      ? 'Purse: $' + (raceDetails?.Purse | numberWithCommas: 0)
                      : ''
                  }}
                </span>

                <!-- Program shortcut — only for tracks selling one today -->
                @if (hasProgram()) {
                  <span
                    (click)="goToProgram()"
                    class="highlight race-program tw-text-[#1E3F7F] tw-cursor-pointer"
                    data-cy="race-program"
                  >
                    <mat-icon class="race-program__icon">description</mat-icon>
                    {{ 'wager.program' | translate: 'PRGM' }}
                  </span>
                }

              </p>
            </div>

            <div class="event-races-section tw-mt-[5px]">
              <!-- Event selection -->
              <!-- <mat-form-field> -->
                <mat-select
                  #eventSelection
                  name="event"
                  placeholder="{{ 'ui.wager.select.horse.races' | translate: 'Select Horse Races' }}"
                  panelClass="select-event-track"
                  class="
                    horse-races
                    tw-float-left
                  "
                  (selectionChange)="selectEvent()"
                  (click)="clickEventList()"
                  [(ngModel)]="currentEventId"
                  [disableOptionCentering]="true"
                  data-cy="wager-events-dropdown"
                >
                  <mat-option
                    class="event-item"
                    [value]="event.id"
                    *ngFor="
                      let event of (events | sortEvents: sortBy: eventTypeFilter: setListFavorites);
                      trackBy: trackByFn
                    "
                  >
                    <span data-cy="event-name">
                      {{
                        event?.CountryCode
                          ? event?.CountryCode === 'US'|| event?.CountryCode === 'CA'
                            ? event?.ToteResponse?.EventDetail?.EventName
                            : event?.CountryCode + '-' + event?.ToteResponse?.EventDetail?.EventName
                          : event?.ToteResponse?.EventDetail?.EventName
                      }}
                    </span>

                    <span data-cy="current-race">
                      {{
                        event?.ToteResponse?.EventDetail?.currentRace?.RaceId
                        ?
                          '('+(event?.ToteResponse?.EventDetail?.TrackType | getTrackType).charAt(0)
                           +
                          ' R'+event?.ToteResponse?.EventDetail?.currentRace?.RaceId+')'
                        : ''
                      }}
                    </span>

                    <span data-cy="event-status">
                      {{
                        event?.ToteResponse?.EventDetail?.currentRace
                          ? (
                              (event?.ToteResponse?.EventDetail?.currentRace)?.RaceStatus
                                | getRaceStatus: (
                                  event?.ToteResponse?.EventDetail?.currentRace
                                )?.PostTime: event?.eventName
                                | countdown: false: event?.eventName | async
                            )
                          : (event?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)
                      }}
                    </span>
                  </mat-option>
                </mat-select>
              <!-- </mat-form-field> -->

              <!-- Race -->
              <feature-mat-race
                [selectedEvent]="selectedEvent"
                [selectedModifier]="selectedModifier"
                [currentSelectedFromEventStatus]="
                  selectedEvent?.ToteResponse?.EventDetail?.currentRace
                    ? (
                        (selectedEvent?.ToteResponse?.EventDetail?.currentRace)?.RaceStatus
                        | getRaceStatus: (
                          selectedEvent?.ToteResponse?.EventDetail?.currentRace
                        )?.PostTime: selectedEvent?.eventName
                        | countdown: false: selectedEvent?.eventName | async
                      )
                    : (selectedEvent?.ToteResponse?.EventDetail?.EventStatus | getEventStatus)"
                (doCloseVideoFn)="closeVideo($event)"
                (doSetRaceStatusFn)="doSetRaceStatus($event)"
                (doGetRaceFn)="doGetPool($event)"
                class="tw-float-left"
              />
            </div>

            <!-- Reserve right space so these rows clear the absolute side icons -->
            <div class="tw-pr-[70px]">
              <!-- Pools -->
              <feature-mat-pool
                [selectedEvent]="selectedEvent"
                [showMoreTrackInfo]="showMoreTrackInfo"
                (doGetPoolFn)="doGetPool($event)"
                class="tw-block"
              />

              <!-- Modifiers -->
              <feature-mat-modifier
                [selectedEvent]="selectedEvent"
                [selectedPool]="selectedPool"
                (doGetModifierFn)="doGetModifier($event)"
                class="tw-block"
              />

              <!-- Bet Amounts -->
              <feature-mat-bets-amount
                [selectedEvent]="selectedEvent"
                [selectedPool]="selectedPool"
                (doGetAmountFn)="doGetBetAmount($event)"
                class="tw-block"
              />
            </div>

            <!-- Closed race -->
            @if ((selectedRace?.RaceStatus | getRaceStatus) !== 'F') {
              <div class="tw-mt-[5px]">
                @if (
                  selectedRace?.RaceStatus === 1
                  || selectedRace?.RaceStatus === 2
                  || selectedRace?.RaceStatus === 4
                  || (selectedEvent?.ToteResponse?.EventDetail?.EventStatus === 'Closed')
                ) {
                  <div
                    class="close-race-section tw-text-sm tw-bg-[#F7D82E] tw-rounded-[5px] tw-p-2"
                  >
                    <p data-cy="closed-race-message">
                      {{ 'wager.raceclosedmessage' | translate: 'Sorry wagering is unavailable, this race is closed' }}
                    </p>
                  </div>
                }
              </div>
            }

            <!-- Play video -->
            <div
              [class.tw-opacity-30]="!isLiveVideoAllowed()"
              [class.tw-pointer-events-none]="!isLiveVideoAllowed()"
              class="
                tw-w-[43px]
                tw-absolute
                tw-top-[0]
                tw-right-[0]
                tw-top-[5px]
                tw-right-[18px]
              "
            >
              <!-- Video -->
              <div
                (click)="playVideo()"
                [ngClass]="
                  {'tw-bg-[#000000]': selectedRace?.RaceStatus === 3}
                "
                class="
                  play-video
                  play-video-img
                  tw-cursor-pointer
                  tw-block
                  tw-bg-[#133964]
                  tw-border-[#A5A5A5]
                  tw-border-[1px]
                  tw-text-[#FFFFFF]
                  tw-text-[38px]
                  tw-py-[5px]
                  tw-px-[7px]
                  tw-rounded-[4px]
                "
                data-cy="wager-video-button"
              >
                <img
                  class="play-icon"
                  src="assets/images/wager/play_event_icon.svg"
                  alt="{{ 'ui.wager.play.icon' | translate: 'play icon' }}"
                  data-cy="play-video"
                >
              </div>

              <div class="tw-text-center tw-mt-[4px]">
                <p class="tw-text-[#FF6F6E] tw-text-[12px]">
                  {{ 'ui.wager.live' | translate: 'Live' }}
                </p>
              </div>
            </div>

            <!-- side icons section (Weather / Info / Notes) -->
            <div
              class="
                side-icons-section
                tw-w-[43px]
                tw-absolute
                tw-right-[18px]
                tw-top-[130px]
                tw-flex
                tw-flex-col
                tw-items-start
              "
              data-cy="wager-side-icons"
            >
              <!-- Weather -->
              <div
                (click)="openWeather()"
                class="
                  side-icon
                  tw-cursor-pointer
                  tw-select-none
                  tw-transition-opacity
                  tw-duration-150
                  hover:tw-opacity-80
                  tw-border-[#ADADAD]
                  tw-border-t-[1px]
                  tw-border-b-[1px]
                  tw-pt-[6px]
                  tw-pb-[3px]
                "
                data-cy="wager-weather-icon"
                title="{{ 'ui.wager.weather' | translate: 'Weather' }}"
              >
                <img
                  src="/assets/images/wager/cloud_icon_light.svg"
                  class="l-mode tw-block tw-h-[26px] tw-w-auto"
                  alt="{{ 'ui.wager.weather.icon' | translate: 'weather icon' }}"
                />
                <img
                  src="/assets/images/wager/cloud_icon_dark.svg"
                  class="d-mode tw-block tw-h-[26px] tw-w-auto"
                  alt="{{ 'ui.wager.weather.icon' | translate: 'weather icon' }}"
                />
              </div>

              <!-- Information -->
              <div
                (click)="openInformation()"
                class="
                  side-icon
                  tw-cursor-pointer
                  tw-select-none
                  tw-transition-opacity
                  tw-duration-150
                  hover:tw-opacity-80
                  tw-border-[#ADADAD]
                  tw-border-b-[1px]
                  tw-pt-[6px]
                  tw-pb-[3px]
                "
                [class.side-icon-active]="showMoreTrackInfo"
                [attr.aria-expanded]="showMoreTrackInfo"
                data-cy="wager-info-icon"
                title="{{ 'ui.wager.information' | translate: 'Information' }}"
              >
                <img
                  src="/assets/images/wager/info_icon_light.svg"
                  class="l-mode tw-block tw-h-[26px] tw-w-auto"
                  alt="{{ 'ui.wager.information.icon' | translate: 'information icon' }}"
                />
                <img
                  src="/assets/images/wager/info_icon_dark.svg"
                  class="d-mode tw-block tw-h-[26px] tw-w-auto"
                  alt="{{ 'ui.wager.information.icon' | translate: 'information icon' }}"
                />
              </div>

              <!-- Notes -->
              <div
                (click)="openNotes()"
                class="
                  side-icon
                  tw-cursor-pointer
                  tw-select-none
                  tw-transition-opacity
                  tw-duration-150
                  hover:tw-opacity-80
                  tw-border-[#ADADAD]
                  tw-border-b-[1px]
                  tw-pt-[6px]
                  tw-pb-[3px]
                "
                data-cy="wager-note-icon"
                title="{{ 'STABLE_SYSTEM.NOTES' | translate: 'Notes' }}"
              >
                <img
                  src="/assets/images/wager/note_icon_light.svg"
                  class="l-mode tw-block tw-h-[24px] tw-w-auto"
                  alt="{{ 'ui.wager.notes.icon' | translate: 'notes icon' }}"
                />
                <img
                  src="/assets/images/wager/note_icon_dark.svg"
                  class="d-mode tw-block tw-h-[24px] tw-w-auto"
                  alt="{{ 'ui.wager.notes.icon' | translate: 'notes icon' }}"
                />
              </div>
            </div>
          </div>

          <!-- runner info basic stats replays section -->
          <div
            class="
              runner-info-basic-stats-replays-section
              tw-rounded-[13px]
              tw-bg-[#E4ECF8]
              tw-pt-[25px]
              tw-pr-[15px]
              tw-pb-[11px]
              tw-pl-[15px]
            "
          >
            <!-- Runners -->
            <feature-mat-runner
              [selectedRace]="selectedRace!"
              [selectedEvent]="selectedEvent"
              [selectedPool]="selectedPool"
              [selectedModifier]="selectedModifier"
              [selectedAmount]="selectedAmount"
              [dblPkArray]="dblPkArray"
              (doShowBetFn)="doShowBet($event)"
              (replayRequested)="onReplayRequested()"
              (displayedRaceChange)="onDisplayedRaceChange($event)"
            />
          </div>

        </div>

        <div class="tw-w-[40%] tw-mt-[8px]">

          <!-- my bets section -->
          <feature-mat-bets/>

          <!-- bet preview section -->
          <feature-mat-bet-preview
            [selectedRace]="selectedRace!"
            [selectedEvent]="selectedEvent"
            [selectedPool]="selectedPool"
            [selectedModifier]="selectedModifier"
            [events]="events"
            [selectedAmount]="selectedAmount"
            [storedSelectedRunners]="showBetPayload?.storedSelectedRunners || ''"
            [showBetPayload]="showBetPayload"
          />

          <!-- pool data (matrix) section -->
          <feature-mat-matrix
            [selectedRaceInput]="selectedRace"
            [selectedEventInput]="selectedEvent"
            [isOnline]="isOnline"
            [currentRaceStatus]="currentRaceStatus"
            (playVideoRequested)="playVideo()"
          />
        </div>
      </div>

    </section>
</ng-template>

<!--
  Full-page skeleton for the very first events fetch. Mirrors
  the real layout; SR announcement handled by HorseLoaderService.
-->
<ng-template #refInitialSkeleton>
  <section
    class="wager-content wager-skeleton tw-px-[28px]"
    aria-hidden="true"
    data-cy="wager-loading-skeleton"
  >
    <!-- Favorites row skeleton — signed-in users only. -->
    @if (userEmail !== undefined && userEmail !== 'Guest') {
      <div
        class="favorites-section tw-mt-[12px]"
        data-cy="wager-favorites-row-skeleton"
      >
        <div class="wager-skeleton__bar tw-w-[24px] tw-h-[24px] tw-rounded-[4px] tw-mr-[19px] tw-float-left"></div>
        <div class="wager-skeleton__bar tw-w-[80px] tw-h-[22px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-float-left"></div>
        <div class="wager-skeleton__bar tw-w-[25px] tw-h-[25px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-ml-[20px] tw-mr-[20px] tw-float-left"></div>
        <div class="favorite-lists tw-w-[80%] tw-flex tw-mt-[5px] tw-float-left">
          @for (chip of [1,2,3,4]; track chip) {
            <div class="wager-skeleton__bar tw-w-[273px] tw-h-[30px] tw-rounded-[4px] tw-mr-[18px] tw-flex-shrink-0"></div>
          }
        </div>
        <div class="wager-skeleton__bar tw-w-[25px] tw-h-[25px] tw-rounded-[4px] tw-relative tw-top-[3px] tw-mt-[5px] tw-ml-[20px] tw-mr-[20px] tw-float-left"></div>
      </div>
    }

    <div class="tw-flex tw-mt-[18px]">
      <div class="tw-w-[60%] tw-mr-[20px]">
        <div
          class="
            wager-skeleton__panel
            tw-rounded-[13px]
            tw-bg-[#EDF1F5]
            tw-mb-[20px]
            tw-p-[20px]
          "
        >
          <div class="wager-skeleton__bar tw-h-[22px] tw-w-[70%] tw-rounded-[4px] tw-mb-[16px]"></div>
          <div class="wager-skeleton__bar tw-h-[40px] tw-w-[292px] tw-rounded-[20px] tw-mb-[18px]"></div>
          <div class="wager-skeleton__row tw-flex tw-gap-[10px] tw-mb-[14px]">
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[64px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[64px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[64px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[64px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[64px] tw-rounded-[6px]"></div>
          </div>
          <div class="wager-skeleton__row tw-flex tw-gap-[10px] tw-mb-[14px]">
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[80px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[80px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[80px] tw-rounded-[6px]"></div>
          </div>
          <div class="wager-skeleton__row tw-flex tw-gap-[10px]">
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[54px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[54px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[54px] tw-rounded-[6px]"></div>
            <div class="wager-skeleton__bar tw-h-[28px] tw-w-[54px] tw-rounded-[6px]"></div>
          </div>
        </div>

        <div
          class="
            wager-skeleton__panel
            tw-rounded-[13px]
            tw-bg-[#E4ECF8]
            tw-p-[20px]
          "
        >
          <div class="wager-skeleton__bar tw-h-[18px] tw-w-[40%] tw-rounded-[4px] tw-mb-[14px]"></div>
          @for (row of [1,2,3,4,5]; track row) {
            <div class="tw-flex tw-items-center tw-gap-[12px] tw-mb-[10px]">
              <div class="wager-skeleton__bar tw-h-[36px] tw-w-[36px] tw-rounded-full"></div>
              <div class="wager-skeleton__bar tw-h-[18px] tw-flex-1 tw-rounded-[4px]"></div>
              <div class="wager-skeleton__bar tw-h-[18px] tw-w-[60px] tw-rounded-[4px]"></div>
              <div class="wager-skeleton__bar tw-h-[18px] tw-w-[60px] tw-rounded-[4px]"></div>
            </div>
          }
        </div>
      </div>

      <div class="tw-w-[40%] tw-mt-[-10px]">
        <div class="wager-skeleton__panel tw-rounded-[13px] tw-bg-[#EDF1F5] tw-p-[18px] tw-mb-[16px]">
          <div class="wager-skeleton__bar tw-h-[18px] tw-w-[35%] tw-rounded-[4px] tw-mb-[14px]"></div>
          <div class="wager-skeleton__bar tw-h-[64px] tw-w-full tw-rounded-[8px]"></div>
        </div>

        <div class="wager-skeleton__panel tw-rounded-[13px] tw-bg-[#EDF1F5] tw-p-[18px] tw-mb-[16px]">
          <div class="wager-skeleton__bar tw-h-[18px] tw-w-[45%] tw-rounded-[4px] tw-mb-[14px]"></div>
          <div class="wager-skeleton__bar tw-h-[110px] tw-w-full tw-rounded-[8px]"></div>
        </div>

        <div class="wager-skeleton__panel tw-rounded-[13px] tw-bg-[#EDF1F5] tw-p-[18px]">
          <div class="wager-skeleton__bar tw-h-[18px] tw-w-[30%] tw-rounded-[4px] tw-mb-[14px]"></div>
          <div class="wager-skeleton__bar tw-h-[160px] tw-w-full tw-rounded-[8px]"></div>
        </div>
      </div>
    </div>
  </section>
</ng-template>

<!--
  Empty / error card. Adapts copy for `isSystemError` and
  offers a Try-again action (no full page reload).
-->
<ng-template #refEmptyState>
  <section
    class="wager-content wager-empty tw-px-[28px]"
    data-cy="wager-empty-state"
  >
    <div class="wager-empty__card" role="status" aria-live="polite">
      <div class="wager-empty__illustration" aria-hidden="true">
        <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wagerEmptyGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#557DAC"/>
              <stop offset="1" stop-color="#1E3F7F"/>
            </linearGradient>
          </defs>
          <circle cx="60" cy="60" r="56" fill="url(#wagerEmptyGrad)" opacity="0.12"/>
          <circle cx="60" cy="60" r="42" fill="url(#wagerEmptyGrad)" opacity="0.2"/>
          <path
            d="M40 78c0-14 9-24 22-24 6 0 11 2 15 6l6-6 3 3-6 6c3 4 5 9 5 15v6H40v-6zm22-18c-9 0-16 6-17 15h34c-1-9-8-15-17-15zm-8-14a5 5 0 1 1 10 0 5 5 0 0 1-10 0z"
            fill="#1E3F7F"
          />
        </svg>
      </div>

      <h3 class="wager-empty__title" data-cy="wager-empty-title">
        @if (isSystemError) {
          {{ 'ui.wager.tote.unreachable' | translate }}
        }
        @else {
          {{ 'ui.wager.no.races.scheduled' | translate }}
        }
      </h3>

      <p class="wager-empty__message" data-cy="wager-empty-message">
        @if (isSystemError) {
          {{ SERVER_ERROR_MESSAGE || ('ui.wager.events.load.failed' | translate: 'Something went wrong while loading the events. Please try again in a moment.') }}
        }
        @else {
          {{ 'ui.wager.the.card.is.empty.at.the' | translate: 'The card is empty at the moment — check back shortly and fresh events will be waiting for you.' }}
        }
      </p>

      <div class="wager-empty__actions">
        <button
          type="button"
          class="wager-empty__button"
          (click)="reloadEvents()"
          data-cy="wager-empty-retry"
        >
          <mat-icon class="wager-empty__button-icon">refresh</mat-icon>
          {{ 'ui.wager.try.again' | translate: 'Try again' }}
        </button>
      </div>
    </div>
  </section>
</ng-template>
